import { useEffect, useState } from "react";
import { db } from "./firebase";
import { doc, updateDoc, collection, addDoc, onSnapshot, query, where } from "firebase/firestore";
import { inputStyle, btnStyle, btnSecondaryStyle, cardStyle, colors } from "./lib/theme";
import { nextLeagueRound, pairKey } from "./lib/roundRobin";
import { computeStandings } from "./lib/stats";

export default function LeagueSession({ sessionId, live }) {
  var players = live.players || [];
  var names = live.playerNames || {};
  var round = live.currentRound;

  var [rankBy, setRankBy] = useState(live.rankBy || "allTime");
  var [history, setHistory] = useState([]);
  var [sessionMatches, setSessionMatches] = useState([]);
  var [scores, setScores] = useState({}); // court -> [scoreA, scoreB]
  var [swappingOut, setSwappingOut] = useState({}); // court -> uid pending a substitute

  useEffect(function () {
    // League standings/ranking only ever consider league-mode results —
    // social results are recorded but never feed into league ranking.
    var q = rankBy === "session"
      ? query(collection(db, "matches"), where("mode", "==", "league"), where("templateId", "==", live.templateId))
      : query(collection(db, "matches"), where("mode", "==", "league"));
    var unsub = onSnapshot(q, function (snap) {
      setHistory(snap.docs.map(function (d) { return d.data(); }));
    });
    return unsub;
  }, [rankBy, live.templateId]);

  useEffect(function () {
    var unsub = onSnapshot(query(collection(db, "matches"), where("sessionId", "==", sessionId)), function (snap) {
      setSessionMatches(snap.docs.map(function (d) { return d.data(); }));
    });
    return unsub;
  }, [sessionId]);

  function name(uid) { return names[uid] || uid; }
  function teamName(team) { return team.map(name).join(" & "); }

  // computeStandings also returns everyone who has ever appeared in a past
  // league match, so it must be narrowed to tonight's roster — otherwise
  // absent members get put on courts (and show as raw IDs, since their names
  // aren't in this session's name list).
  function tonightStandings() {
    return computeStandings(history, players, names).filter(function (s) { return players.indexOf(s.uid) !== -1; });
  }

  function startRound() {
    var rankedIds = tonightStandings().map(function (s) { return s.uid; });
    var pairing = nextLeagueRound(rankedIds, live.courtCount, live.leagueStats, 6);
    var courts = pairing.courts.map(function (c) {
      return { court: c.court, teamA: c.teamA, teamB: c.teamB, completed: false };
    });
    updateDoc(doc(db, "liveSessions", sessionId), {
      rankBy: rankBy,
      round: (live.round || 0) + 1,
      currentRound: {
        roundNumber: (live.round || 0) + 1,
        courts: courts,
        sitOut: pairing.sitOut,
      },
      leagueStats: pairing.stats,
    });
    setScores({});
  }

  function submitCourtScore(court) {
    var s = scores[court.court] || [0, 0];
    var sA = parseInt(s[0], 10) || 0;
    var sB = parseInt(s[1], 10) || 0;
    var winningTeam = sA === sB ? null : (sA > sB ? "A" : "B");

    addDoc(collection(db, "matches"), {
      sessionId: sessionId,
      templateId: live.templateId,
      occurrenceDate: live.occurrenceDate,
      venueId: live.venueId,
      venueName: live.venueName,
      mode: "league",
      court: court.court,
      teamA: court.teamA,
      teamB: court.teamB,
      players: court.teamA.concat(court.teamB),
      scoreA: sA,
      scoreB: sB,
      winningTeam: winningTeam,
      timestamp: new Date().toISOString(),
    });

    var updatedCourts = round.courts.map(function (c) {
      return c.court === court.court ? { ...c, completed: true, scoreA: sA, scoreB: sB } : c;
    });
    updateDoc(doc(db, "liveSessions", sessionId), {
      currentRound: { ...round, courts: updatedCourts },
    });
  }

  // Rests `outUid` for the rest of this round and brings `inUid` in on the
  // same court/team, keeping fairness tracking accurate for future rounds.
  function swapPlayer(court, outUid, inUid) {
    var onA = court.teamA.indexOf(outUid) !== -1;
    var partner = (onA ? court.teamA : court.teamB).find(function (u) { return u !== outUid; });
    var opponents = onA ? court.teamB : court.teamA;

    var stats = live.leagueStats || { gamesPlayed: {}, partnerCount: {}, opponentCount: {}, lastRoundTeams: [] };
    var gp = { ...stats.gamesPlayed };
    gp[outUid] = Math.max(0, (gp[outUid] || 0) - 1);
    gp[inUid] = (gp[inUid] || 0) + 1;

    var pc = { ...stats.partnerCount };
    var pkOld = pairKey(partner, outUid);
    var pkNew = pairKey(partner, inUid);
    pc[pkOld] = Math.max(0, (pc[pkOld] || 0) - 1);
    pc[pkNew] = (pc[pkNew] || 0) + 1;

    var oc = { ...stats.opponentCount };
    opponents.forEach(function (o) {
      var okOld = pairKey(o, outUid);
      var okNew = pairKey(o, inUid);
      oc[okOld] = Math.max(0, (oc[okOld] || 0) - 1);
      oc[okNew] = (oc[okNew] || 0) + 1;
    });

    var updatedCourts = round.courts.map(function (c) {
      if (c.court !== court.court) return c;
      return {
        ...c,
        teamA: onA ? c.teamA.map(function (u) { return u === outUid ? inUid : u; }) : c.teamA,
        teamB: !onA ? c.teamB.map(function (u) { return u === outUid ? inUid : u; }) : c.teamB,
      };
    });
    var updatedSitOut = round.sitOut.filter(function (u) { return u !== inUid; }).concat([outUid]);

    updateDoc(doc(db, "liveSessions", sessionId), {
      currentRound: { ...round, courts: updatedCourts, sitOut: updatedSitOut },
      leagueStats: { gamesPlayed: gp, partnerCount: pc, opponentCount: oc, lastRoundTeams: stats.lastRoundTeams },
    });
    setSwappingOut(function (prev) { var next = { ...prev }; delete next[court.court]; return next; });
  }

  var allCompleted = round && round.courts.every(function (c) { return c.completed; });
  var sessionStandings = computeStandings(sessionMatches, players, names).filter(function (s) { return s.games > 0; });

  if (!round) {
    var standingsPreview = tonightStandings();
    return (
      <div style={cardStyle}>
        <h3 style={{ marginTop: 0, fontSize: 15 }}>Start League Play</h3>
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, color: colors.textDim, marginBottom: 5, textTransform: "uppercase", letterSpacing: 1 }}>Rank Players By</div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={function () { setRankBy("allTime"); }} style={rankBy === "allTime" ? btnStyle : btnSecondaryStyle}>All-Time Results</button>
            <button onClick={function () { setRankBy("session"); }} style={rankBy === "session" ? btnStyle : btnSecondaryStyle}>This Session's History</button>
          </div>
        </div>
        <div style={{ marginBottom: 14 }}>
          {standingsPreview.map(function (s) {
            return (
              <div key={s.uid} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "4px 0", borderBottom: "1px solid " + colors.border }}>
                <span>{s.name}</span>
                <span style={{ color: colors.textDim }}>{s.games > 0 ? Math.round(s.winPct * 100) + "% (" + s.wins + "-" + s.losses + ")" : "no games yet"}</span>
              </div>
            );
          })}
        </div>
        <p style={{ fontSize: 12, color: colors.textDim }}>
          {players.length + " players → " + live.courtCount + " court(s), doubles. Each round, courts are grouped by "
            + "nearby rank (roughly ±6 places) rather than one fixed group all night — who's on which court will "
            + "shift round to round as fairness rotates who's due a game."}
        </p>
        {players.length < 4 ? (
          <p style={{ fontSize: 13, color: colors.danger }}>Need at least 4 players for a doubles match.</p>
        ) : (
          <button style={btnStyle} onClick={startRound}>Start Round 1</button>
        )}
      </div>
    );
  }

  return (
    <div>
      <h3 style={{ margin: "0 0 12px", fontSize: 16 }}>{"Round " + round.roundNumber}</h3>

      {round.courts.map(function (c) {
        var s = scores[c.court] || ["", ""];
        var subCandidates = round.sitOut;
        return (
          <div key={c.court} style={{ ...cardStyle, marginBottom: 12 }}>
            <div style={{ fontSize: 12, color: colors.textDim, marginBottom: 6 }}>{"Court " + c.court}</div>
            {c.completed ? (
              <div style={{ fontSize: 14 }}>
                {teamName(c.teamA)} {c.scoreA} — {c.scoreB} {teamName(c.teamB)}
              </div>
            ) : (
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: subCandidates.length > 0 ? 10 : 0 }}>
                  <div style={{ flex: 1, fontSize: 14 }}>{teamName(c.teamA)}</div>
                  <input style={{ ...inputStyle, width: 60 }} type="number" value={s[0]} onChange={function (e) {
                    setScores(function (prev) { return { ...prev, [c.court]: [e.target.value, s[1]] }; });
                  }} />
                  <span style={{ color: colors.textDim }}>vs</span>
                  <input style={{ ...inputStyle, width: 60 }} type="number" value={s[1]} onChange={function (e) {
                    setScores(function (prev) { return { ...prev, [c.court]: [s[0], e.target.value] }; });
                  }} />
                  <div style={{ flex: 1, fontSize: 14, textAlign: "right" }}>{teamName(c.teamB)}</div>
                </div>
                {subCandidates.length > 0 && (
                  <div style={{ display: "flex", gap: 8 }}>
                    <select
                      style={{ ...inputStyle, fontSize: 12, padding: "6px 8px" }}
                      value={swappingOut[c.court] || ""}
                      onChange={function (e) {
                        var outUid = e.target.value;
                        setSwappingOut(function (prev) { return { ...prev, [c.court]: outUid }; });
                      }}
                    >
                      <option value="">Rest a player…</option>
                      {c.teamA.concat(c.teamB).map(function (uid) { return <option key={uid} value={uid}>{name(uid)}</option>; })}
                    </select>
                    {swappingOut[c.court] && (
                      <select
                        style={{ ...inputStyle, fontSize: 12, padding: "6px 8px" }}
                        value=""
                        onChange={function (e) {
                          if (e.target.value) swapPlayer(c, swappingOut[c.court], e.target.value);
                        }}
                      >
                        <option value="">Bring in…</option>
                        {subCandidates.map(function (uid) { return <option key={uid} value={uid}>{name(uid)}</option>; })}
                      </select>
                    )}
                  </div>
                )}
              </div>
            )}
            {!c.completed && (
              <button style={{ ...btnSecondaryStyle, marginTop: 10 }} onClick={function () { submitCourtScore(c); }}>Submit Score</button>
            )}
          </div>
        );
      })}

      {round.sitOut.length > 0 && (
        <p style={{ fontSize: 12, color: colors.textDim }}>{"Sitting out this round: " + round.sitOut.map(name).join(", ")}</p>
      )}

      {allCompleted && (
        <button style={btnStyle} onClick={startRound}>{"Start Round " + (round.roundNumber + 1)}</button>
      )}

      {sessionStandings.length > 0 && (
        <div style={{ ...cardStyle, marginTop: 16 }}>
          <h4 style={{ marginTop: 0, fontSize: 13 }}>Standings So Far Tonight</h4>
          {sessionStandings.map(function (row) {
            return (
              <div key={row.uid} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "3px 0" }}>
                <span>{row.name}</span>
                <span style={{ color: colors.textDim }}>{row.wins}W - {row.losses}L</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
